import { toGregorian, toJalaali } from "jalaali-js";

/** Convert Persian picker string `y/m/d` to a `Date` (local noon, for stable API serialization). */
/** Parse picker string `y/m/d` into day parts for `PersianDatePicker` `initialDay`. */
export function parsePersianDayString(
  s: string | undefined | null,
): { year: number; month: number; day: number } | undefined {
  if (s == null || !String(s).trim()) return undefined;
  const parts = String(s)
    .split("/")
    .map((x) => parseInt(x.trim(), 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return undefined;
  return { year: parts[0], month: parts[1], day: parts[2] };
}

export function persianDateStringToLocalDate(
  s: string | undefined | null,
): Date | undefined {
  if (s == null || !String(s).trim()) return undefined;
  const parts = String(s)
    .split("/")
    .map((x) => parseInt(x.trim(), 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return undefined;
  const [jy, jm, jd] = parts;
  const { gy, gm, gd } = toGregorian(jy, jm, jd);
  return new Date(gy, gm - 1, gd, 12, 0, 0, 0);
}

/** ISO / API `DateTime` → Persian `y/m/d` for the form date field. */
export function isoOrDateToPersianDayString(
  iso: string | Date | null | undefined,
): string {
  if (iso == null || iso === "") return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "";
  const j = toJalaali(d);
  return `${j.jy}/${j.jm}/${j.jd}`;
}

/** Today in the Persian (Jalali) calendar as `{ year, month, day }` for date pickers. */
export function getTodayPersianCalendarDay(): {
  year: number;
  month: number;
  day: number;
} {
  const now = new Date();
  try {
    const fmt = new Intl.DateTimeFormat("en-GB", {
      calendar: "persian",
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
    const parts = fmt.formatToParts(now);
    let year = 1400;
    let month = 1;
    let day = 1;
    for (const p of parts) {
      if (p.type === "year") year = Number(p.value);
      if (p.type === "month") month = Number(p.value);
      if (p.type === "day") day = Number(p.value);
    }
    return { year, month, day };
  } catch {
    return { year: 1400, month: 1, day: 1 };
  }
}

export function formatPersianDayString(day: {
  year: number;
  month: number;
  day: number;
}): string {
  return `${day.year}/${day.month}/${day.day}`;
}

export function formatTodayShamsiDate(): string {
  try {
    return new Intl.DateTimeFormat("fa-IR", {
      calendar: "persian",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return formatPersianDayString(getTodayPersianCalendarDay());
  }
}

/** Format an absolute instant as a Jalali date in the requested display locale. */
export function formatShamsiDate(
  iso: string | Date | null | undefined,
  locale = "fa-IR",
): string {
  if (iso == null || iso === "") return "—";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(locale, {
      calendar: "persian",
      timeZone: "Asia/Tehran",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return "—";
  }
}

/**
 * Format an ISO/Gregorian instant as Jalali (Shamsi) date–time for fa-IR display.
 */
export function formatShamsiDateTime(
  iso: string | Date | null | undefined
): string {
  if (iso == null || iso === "") return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("fa-IR", {
      calendar: "persian",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "—";
  }
}
